import React, { useRef } from 'react';
import { motion, useInView } from 'framer-motion';
import { SuitDivider } from './SuitDivider';
export function Observatory() {
  const ref = useRef<HTMLDivElement>(null);
  const isInView = useInView(ref, {
    once: true,
    margin: '-100px'
  });
  const nodeVariants = {
    hidden: {
      scale: 0,
      opacity: 0
    },
    visible: (i: number) => ({
      scale: 1,
      opacity: 1,
      transition: {
        type: 'spring',
        stiffness: 200,
        damping: 20,
        delay: 0.3 + i * 0.18
      }
    })
  };
  const lineVariants = {
    hidden: {
      pathLength: 0,
      opacity: 0
    },
    visible: (i: number) => ({
      pathLength: 1,
      opacity: 1,
      transition: {
        pathLength: {
          duration: 0.8,
          delay: 0.5 + i * 0.18,
          ease: 'easeInOut'
        },
        opacity: {
          duration: 0.2,
          delay: 0.5 + i * 0.18
        }
      }
    })
  };
  const labelVariants = {
    hidden: {
      opacity: 0
    },
    visible: (i: number) => ({
      opacity: 1,
      transition: {
        duration: 0.5,
        delay: 1.0 + i * 0.18
      }
    })
  };
  // Tree layout coordinates (SVG viewBox 800x500)
  // Level 0: Preflop (400, 60)
  // Level 1: Fold (140, 200), Call (400, 200), Raise (660, 200)
  // Level 2 (from Call): Flop (400, 340)
  // Level 3 (from Flop): Check (280, 460), Bet (520, 460)
  return <section className="relative bg-felt felt-texture py-24 md:py-32" data-id="element-95">
      <div className="max-w-6xl mx-auto px-6" data-id="element-96">
        {/* Section header */}
        <div className="text-center mb-16" data-id="element-97">
          <motion.h2 className="font-display text-3xl md:text-4xl lg:text-5xl text-goldenrod uppercase tracking-[0.3em] mb-4" initial={{
          opacity: 0,
          y: 30
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} transition={{
          duration: 0.8
        }} data-id="element-98">
            The Observatory
          </motion.h2>
          <motion.p className="font-body text-goldenrod/70 italic text-lg tracking-wide" initial={{
          opacity: 0
        }} whileInView={{
          opacity: 1
        }} viewport={{
          once: true
        }} transition={{
          duration: 0.8,
          delay: 0.2
        }} data-id="element-99">
            Decision Tree Cartography
          </motion.p>
          <SuitDivider className="mt-8 max-w-md mx-auto" data-id="element-100" />
        </div>

        {/* Probability tree SVG */}
        <div ref={ref} className="relative max-w-4xl mx-auto" data-id="element-101">
          {/* Decorative frame */}
          <div className="absolute inset-0 border border-goldenrod/20 -m-4 md:-m-6" data-id="element-102" />
          <div className="absolute -top-4 -left-4 md:-top-6 md:-left-6 w-8 h-8 border-t-2 border-l-2 border-goldenrod" data-id="element-103" />
          <div className="absolute -top-4 -right-4 md:-top-6 md:-right-6 w-8 h-8 border-t-2 border-r-2 border-goldenrod" data-id="element-104" />
          <div className="absolute -bottom-4 -left-4 md:-bottom-6 md:-left-6 w-8 h-8 border-b-2 border-l-2 border-goldenrod" data-id="element-105" />
          <div className="absolute -bottom-4 -right-4 md:-bottom-6 md:-right-6 w-8 h-8 border-b-2 border-r-2 border-goldenrod" data-id="element-106" />

          <svg viewBox="0 0 800 520" className="w-full h-auto" role="img" aria-label="Poker decision tree showing preflop actions branching into fold, call, and raise, with call leading to flop decisions of check or bet" data-id="element-107">
            <defs data-id="element-108">
              <filter id="glow" data-id="element-109">
                <feGaussianBlur stdDeviation="2" result="coloredBlur" data-id="element-110" />
                <feMerge data-id="element-111">
                  <feMergeNode in="coloredBlur" data-id="element-112" />
                  <feMergeNode in="SourceGraphic" data-id="element-113" />
                </feMerge>
              </filter>
            </defs>

            {/* Connecting lines */}
            {/* Preflop → Fold */}
            <motion.line x1="400" y1="80" x2="140" y2="190" stroke="#daa520" strokeWidth="1.5" fill="none" variants={lineVariants} custom={0} initial="hidden" animate={isInView ? 'visible' : 'hidden'} data-id="element-114" />
            {/* Preflop → Call */}
            <motion.line x1="400" y1="80" x2="400" y2="190" stroke="#daa520" strokeWidth="1.5" fill="none" variants={lineVariants} custom={1} initial="hidden" animate={isInView ? 'visible' : 'hidden'} data-id="element-115" />
            {/* Preflop → Raise */}
            <motion.line x1="400" y1="80" x2="660" y2="190" stroke="#daa520" strokeWidth="1.5" fill="none" variants={lineVariants} custom={2} initial="hidden" animate={isInView ? 'visible' : 'hidden'} data-id="element-116" />
            {/* Call → Flop */}
            <motion.line x1="400" y1="230" x2="400" y2="330" stroke="#daa520" strokeWidth="1.5" fill="none" variants={lineVariants} custom={3} initial="hidden" animate={isInView ? 'visible' : 'hidden'} data-id="element-117" />
            {/* Flop → Check */}
            <motion.line x1="400" y1="370" x2="280" y2="445" stroke="#daa520" strokeWidth="1.5" fill="none" variants={lineVariants} custom={4} initial="hidden" animate={isInView ? 'visible' : 'hidden'} data-id="element-118" />
            {/* Flop → Bet */}
            <motion.line x1="400" y1="370" x2="520" y2="445" stroke="#daa520" strokeWidth="1.5" fill="none" variants={lineVariants} custom={5} initial="hidden" animate={isInView ? 'visible' : 'hidden'} data-id="element-119" />

            {/* Percentage labels on branches */}
            <motion.text x="245" y="128" textAnchor="middle" fill="#fffdf5" opacity="0.7" fontSize="13" fontFamily="EB Garamond, serif" fontStyle="italic" variants={labelVariants} custom={0} initial="hidden" animate={isInView ? 'visible' : 'hidden'} data-id="element-120">
              Fold 23%
            </motion.text>
            <motion.text x="420" y="140" textAnchor="start" fill="#fffdf5" opacity="0.7" fontSize="13" fontFamily="EB Garamond, serif" fontStyle="italic" variants={labelVariants} custom={1} initial="hidden" animate={isInView ? 'visible' : 'hidden'} data-id="element-121">
              Call 45%
            </motion.text>
            <motion.text x="558" y="128" textAnchor="middle" fill="#fffdf5" opacity="0.7" fontSize="13" fontFamily="EB Garamond, serif" fontStyle="italic" variants={labelVariants} custom={2} initial="hidden" animate={isInView ? 'visible' : 'hidden'} data-id="element-122">
              Raise 32%
            </motion.text>
            <motion.text x="420" y="288" textAnchor="start" fill="#fffdf5" opacity="0.7" fontSize="13" fontFamily="EB Garamond, serif" fontStyle="italic" variants={labelVariants} custom={3} initial="hidden" animate={isInView ? 'visible' : 'hidden'} data-id="element-123">
              100%
            </motion.text>
            <motion.text x="318" y="418" textAnchor="middle" fill="#fffdf5" opacity="0.7" fontSize="13" fontFamily="EB Garamond, serif" fontStyle="italic" variants={labelVariants} custom={4} initial="hidden" animate={isInView ? 'visible' : 'hidden'} data-id="element-124">
              Check 58%
            </motion.text>
            <motion.text x="482" y="418" textAnchor="middle" fill="#fffdf5" opacity="0.7" fontSize="13" fontFamily="EB Garamond, serif" fontStyle="italic" variants={labelVariants} custom={5} initial="hidden" animate={isInView ? 'visible' : 'hidden'} data-id="element-125">
              Bet 42%
            </motion.text>

            {/* Nodes - Octagonal/geometric style */}
            {/* Preflop - Root node */}
            <motion.g variants={nodeVariants} custom={0} initial="hidden" animate={isInView ? 'visible' : 'hidden'} filter="url(#glow)" data-id="element-126">
              <polygon points="400,35 425,48 435,73 425,98 400,111 375,98 365,73 375,48" fill="#132e1e" stroke="#daa520" strokeWidth="2" data-id="element-127" />
              <text x="400" y="78" textAnchor="middle" fill="#daa520" fontSize="14" fontFamily="Poiret One, sans-serif" letterSpacing="0.1em" data-id="element-128">
                PREFLOP
              </text>
            </motion.g>

            {/* Fold node */}
            <motion.g variants={nodeVariants} custom={1} initial="hidden" animate={isInView ? 'visible' : 'hidden'} data-id="element-129">
              <polygon points="140,175 160,185 168,205 160,225 140,235 120,225 112,205 120,185" fill="#132e1e" stroke="#daa520" strokeWidth="1.5" data-id="element-130" />
              <text x="140" y="210" textAnchor="middle" fill="#fffdf5" fontSize="12" fontFamily="Poiret One, sans-serif" letterSpacing="0.08em" opacity="0.9" data-id="element-131">
                FOLD
              </text>
            </motion.g>

            {/* Call node */}
            <motion.g variants={nodeVariants} custom={2} initial="hidden" animate={isInView ? 'visible' : 'hidden'} filter="url(#glow)" data-id="element-132">
              <polygon points="400,175 425,188 435,210 425,232 400,245 375,232 365,210 375,188" fill="#132e1e" stroke="#daa520" strokeWidth="2" data-id="element-133" />
              <text x="400" y="215" textAnchor="middle" fill="#daa520" fontSize="13" fontFamily="Poiret One, sans-serif" letterSpacing="0.08em" data-id="element-134">
                CALL
              </text>
            </motion.g>

            {/* Raise node */}
            <motion.g variants={nodeVariants} custom={3} initial="hidden" animate={isInView ? 'visible' : 'hidden'} data-id="element-135">
              <polygon points="660,175 680,185 688,205 680,225 660,235 640,225 632,205 640,185" fill="#132e1e" stroke="#daa520" strokeWidth="1.5" data-id="element-136" />
              <text x="660" y="210" textAnchor="middle" fill="#fffdf5" fontSize="12" fontFamily="Poiret One, sans-serif" letterSpacing="0.08em" opacity="0.9" data-id="element-137">
                RAISE
              </text>
            </motion.g>

            {/* Flop node */}
            <motion.g variants={nodeVariants} custom={4} initial="hidden" animate={isInView ? 'visible' : 'hidden'} filter="url(#glow)" data-id="element-138">
              <polygon points="400,315 425,328 435,350 425,372 400,385 375,372 365,350 375,328" fill="#132e1e" stroke="#daa520" strokeWidth="2" data-id="element-139" />
              <text x="400" y="355" textAnchor="middle" fill="#daa520" fontSize="14" fontFamily="Poiret One, sans-serif" letterSpacing="0.1em" data-id="element-140">
                FLOP
              </text>
            </motion.g>

            {/* Check node */}
            <motion.g variants={nodeVariants} custom={5} initial="hidden" animate={isInView ? 'visible' : 'hidden'} data-id="element-141">
              <polygon points="280,435 300,445 308,462 300,479 280,489 260,479 252,462 260,445" fill="#132e1e" stroke="#daa520" strokeWidth="1.5" data-id="element-142" />
              <text x="280" y="467" textAnchor="middle" fill="#fffdf5" fontSize="11" fontFamily="Poiret One, sans-serif" letterSpacing="0.08em" opacity="0.9" data-id="element-143">
                CHECK
              </text>
            </motion.g>

            {/* Bet node */}
            <motion.g variants={nodeVariants} custom={6} initial="hidden" animate={isInView ? 'visible' : 'hidden'} data-id="element-144">
              <polygon points="520,435 540,445 548,462 540,479 520,489 500,479 492,462 500,445" fill="#132e1e" stroke="#daa520" strokeWidth="1.5" data-id="element-145" />
              <text x="520" y="467" textAnchor="middle" fill="#fffdf5" fontSize="12" fontFamily="Poiret One, sans-serif" letterSpacing="0.08em" opacity="0.9" data-id="element-146">
                BET
              </text>
            </motion.g>
          </svg>
        </div>
      </div>
    </section>;
}